/** Реестр прав `<resource>.<action>` (ТЗ 04 §2). Источник истины — код; в БД хранятся коды в role_permissions. */
const crud = ["view", "create", "update", "delete"] as const;

export const PERMISSION_REGISTRY = {
  dashboard: ["view"],
  pos: ["view"],
  orders: [...crud, "cancel", "refund", "discount", "void", "reopen", "transfer", "merge", "split", "price_override", "credit_sale", "print"],
  tables: [...crud, "manage", "print"],
  halls: [...crud, "manage"],
  /** Распределение столов между официантами: зоны и настройки */
  waiter_zones: ["view", "manage"],
  kitchen: ["view", "manage"],
  menu: [...crud, "manage", "export"],
  stop_list: ["view", "update"],
  recipes: [...crud, "approve"],
  costing: ["view", "update"],
  warehouses: [...crud],
  stock: ["view", "export"],
  stock_documents: [...crud, "post", "unpost"],
  inventory_counts: [...crud, "post"],
  /** Перемещения между складами (addon.inventory_pro): receive — приёмка на складе-получателе */
  stock_transfers: ["view", "create", "receive", "delete"],
  /** Производство полуфабрикатов (addon.production): complete — провести выпуск */
  production: [...crud, "complete"],
  suppliers: [...crud],
  /** Заявки на закупку: create — писать свои (видны только свои), view — все заявки филиала, manage — одобрять, отказывать, отмечать «куплено» */
  purchase_requests: ["view", "create", "manage"],
  supplier_payments: ["view", "create", "delete"],
  customers: [...crud, "export"],
  debts: ["view", "manage", "adjust", "write_off"],
  payments: ["view", "create", "refund"],
  cash_shifts: ["view", "open_shift", "close_shift", "cash_in", "cash_out", "x_report"],
  reservations: [...crud],
  employees: [...crud],
  work_shifts: ["view", "create", "update", "approve"],
  payroll: ["view", "calculate", "approve", "pay"],
  tips: ["view", "manage"],
  expenses: [...crud, "approve"],
  analytics: ["view", "export", "finance"],
  reports: ["view", "export"],
  /** Сброс отчётов (учёт с чистого листа): view — история и архивы, manage — сбросить / отменить сброс. Отдельно от reports.*, чтобы не досталось управляющему и бухгалтеру */
  report_resets: ["view", "manage"],
  settings: ["view", "manage"],
  branches: [...crud, "manage"],
  users: [...crud, "manage"],
  roles: ["view", "create", "update", "delete", "assign"],
  devices: ["view", "create", "update", "manage"],
  audit: ["view", "export"],
  subscription: ["view", "manage"],
  /** grant_access — разрешить сотруднику Coca войти в кабинет (сеанс поддержки) */
  support: ["view", "create", "grant_access"],
  fraud: ["view", "manage"],
  sms: ["view", "send"],
  services: ["view", "sell", "manage"],
  banquets: ["view", "create", "update", "manage"],
  promotions: ["view", "manage"],
  deliveries: ["view", "manage", "courier"],
  /** Цели и достижения (core.staff): view — цели всех и рейтинг, manage — ставить цели. Свои цели сотрудник видит без права (/me/goals) */
  goals: ["view", "manage"],
} as const satisfies Record<string, readonly string[]>;

type Registry = typeof PERMISSION_REGISTRY;
export type PermissionResource = keyof Registry;
export type Permission = { [R in PermissionResource]: `${R}.${Registry[R][number]}` }[PermissionResource];

export const ALL_PERMISSIONS = Object.entries(PERMISSION_REGISTRY).flatMap(([r, actions]) => actions.map((a) => `${r}.${a}`)) as Permission[];

/** Шаблон: точный код, `resource.*` или `*`. */
export type PermissionPattern = Permission | `${PermissionResource}.*` | "*";

export function expandPermissions(patterns: readonly PermissionPattern[]): Permission[] {
  const set = new Set<Permission>();
  for (const p of patterns) {
    if (p === "*") ALL_PERMISSIONS.forEach((x) => set.add(x));
    else if (p.endsWith(".*")) ALL_PERMISSIONS.filter((x) => x.startsWith(p.slice(0, -1))).forEach((x) => set.add(x));
    else set.add(p as Permission);
  }
  return [...set];
}

export type RoleScope = "company" | "branch";

/** Системные роли (ТЗ 04 §2.4) — копируются в компанию при onboarding. */
export const SYSTEM_ROLES: { code: string; name: string; scope: RoleScope; permissions: PermissionPattern[] }[] = [
  { code: "owner", name: "Владелец", scope: "company", permissions: ["*"] },
  { code: "company_admin", name: "Администратор компании", scope: "company", permissions: ["*"] },
  {
    code: "branch_manager",
    name: "Управляющий филиалом",
    scope: "branch",
    permissions: ["dashboard.*", "pos.*", "orders.*", "tables.*", "halls.*", "waiter_zones.*", "kitchen.*", "menu.*", "stop_list.*", "recipes.*", "costing.*", "warehouses.view", "stock.*", "stock_documents.*", "inventory_counts.*", "stock_transfers.*", "production.*", "suppliers.*", "purchase_requests.*", "supplier_payments.*", "customers.*", "debts.*", "payments.*", "cash_shifts.*", "reservations.*", "employees.*", "work_shifts.*", "payroll.view", "tips.*", "expenses.*", "analytics.view", "analytics.export", "reports.*", "settings.view", "users.view", "devices.*", "audit.view", "support.*", "services.*", "banquets.*", "promotions.view", "deliveries.*", "goals.*"],
  },
  { code: "accountant", name: "Бухгалтер", scope: "company", permissions: ["dashboard.view", "orders.view", "payments.view", "cash_shifts.view", "expenses.*", "payroll.*", "tips.view", "analytics.*", "reports.*", "debts.view", "stock.view", "stock_documents.view", "stock_transfers.view", "production.view", "suppliers.view", "purchase_requests.view", "supplier_payments.*", "goals.view"] },
  {
    code: "cashier",
    name: "Кассир",
    scope: "branch",
    permissions: ["pos.view", "orders.view", "orders.create", "orders.update", "orders.print", "orders.split", "orders.transfer", "payments.view", "payments.create", "cash_shifts.view", "cash_shifts.open_shift", "cash_shifts.close_shift", "reservations.view", "reservations.create", "reservations.update", "customers.view", "customers.create", "tables.view", "waiter_zones.view", "menu.view", "stop_list.view", "services.view", "services.sell", "deliveries.view", "deliveries.manage", "purchase_requests.create"],
  },
  {
    code: "waiter",
    name: "Официант",
    scope: "branch",
    permissions: ["pos.view", "orders.view", "orders.create", "orders.update", "orders.print", "reservations.view", "reservations.create", "reservations.update", "tables.view", "tables.update", "waiter_zones.view", "menu.view", "stop_list.view", "customers.view", "services.view", "services.sell", "purchase_requests.create"],
  },
  /** Курьер (addon.delivery): только свои доставки и их статусы — экран «Мои доставки» */
  { code: "courier", name: "Курьер", scope: "branch", permissions: ["deliveries.courier", "payments.create"] },
  { code: "cook", name: "Повар", scope: "branch", permissions: ["kitchen.view", "menu.view", "stop_list.view", "stop_list.update", "recipes.view", "production.view", "production.update", "production.complete", "purchase_requests.create"] },
  { code: "auditor", name: "Аудитор", scope: "company", permissions: ALL_PERMISSIONS.filter((p) => p.endsWith(".view")) },
];

/** Человекочитаемые названия ресурсов и действий — для редактора ролей. */
export const RESOURCE_LABELS_RU: Record<PermissionResource, string> = {
  dashboard: "Дашборд", pos: "POS", orders: "Заказы", tables: "Столы", halls: "Залы", waiter_zones: "Официанты по залам", kitchen: "Кухня", menu: "Меню",
  stop_list: "Стоп-лист", recipes: "Рецептуры", costing: "Калькуляция", warehouses: "Склады", stock: "Остатки",
  stock_documents: "Складские документы", inventory_counts: "Инвентаризация", stock_transfers: "Перемещения", production: "Производство", suppliers: "Поставщики", purchase_requests: "Заявки на закупку", supplier_payments: "Оплаты поставщикам", customers: "Клиенты", debts: "Долги",
  payments: "Оплаты", cash_shifts: "Кассовые смены", reservations: "Брони", employees: "Сотрудники", work_shifts: "Рабочие смены", payroll: "Зарплата", tips: "Чаевые",
  expenses: "Расходы", analytics: "Аналитика", reports: "Отчёты", report_resets: "Сброс отчётов", settings: "Настройки", branches: "Филиалы",
  users: "Пользователи", roles: "Роли", devices: "Терминалы", audit: "Аудит", subscription: "Подписка", support: "Поддержка", fraud: "Защита от злоупотреблений", sms: "SMS-рассылки", services: "Услуги и аренда", banquets: "Банкеты", promotions: "Акции", deliveries: "Доставка", goals: "Цели и достижения",
};

export const ACTION_LABELS_RU: Record<string, string> = {
  view: "Просмотр", create: "Создание", update: "Изменение", delete: "Удаление", manage: "Управление", approve: "Подтверждение",
  cancel: "Отмена", refund: "Возврат", discount: "Скидки", void: "Аннулирование", reopen: "Переоткрытие", transfer: "Перенос",
  merge: "Объединение", split: "Разделение", price_override: "Изменение цены", credit_sale: "Продажа в долг", print: "Печать",
  export: "Экспорт", post: "Проведение", unpost: "Отмена проведения", open_shift: "Открытие смены", close_shift: "Закрытие смены",
  cash_in: "Внесение", cash_out: "Изъятие", finance: "Финансы", assign: "Назначение",
  adjust: "Корректировка", receive: "Приёмка", complete: "Проведение выпуска", write_off: "Списание", x_report: "X-отчёт", calculate: "Расчёт", pay: "Выплата",
  grant_access: "Доступ для поддержки",
};
